import type { WorkflowCompletion } from '@generated/graphql';
import { DepositTransferContext } from '@ovComponents/4-module/depositWorkflow/depositTransferContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  type TransferContextData,
  toTransferContextData,
} from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { updateContextTransfer } from '@utils/updateWorkflowContext';
import { omit } from 'lodash-es';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
} from 'react';

/**
 * Custom hook to manage applicableData and its setters across deposit workflow components.
 * This hook determines the appropriate data source and setter functions based on the context state.
 *
 * @param dummyDeposit - Optional dummy deposit data for testing/preview purposes
 * @param setDummyDeposit - Optional setter for dummy deposit data
 * @returns Object containing applicableData and setter functions
 */
const useApplicableData = (
  dummyDeposit?: TransferContextData,
  setDummyDeposit?: Dispatch<SetStateAction<TransferContextData>>,
) => {
  const { transferData, setTransferData, mode } = useContext(
    DepositTransferContext,
  );
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);

  // Determine the applicable data source based on priority:
  // 1. dummyDeposit (for testing/preview)
  // 2. transferData (if it has content)
  // 3. workflowData (fallback)
  const applicableData =
    dummyDeposit ??
    (Object.keys(transferData).length !== 0 ? transferData : workflowData);

  // Determine the application context setter based on the data source
  const setApplicationContext =
    setDummyDeposit ??
    (Object.keys(transferData).length !== 0 ? setTransferData : undefined);

  // Determine the workflow completion context setter
  const setWorkflowCompletionContext =
    Object.keys(transferData).length === 0 ? setWorkflowData : undefined;

  return {
    applicableData,
    setApplicationContext,
    setWorkflowCompletionContext,
    mode,
  };
};

/**
 * Custom hook that handles syncing temporary deposits data with the workflow context.
 * This hook looks for existing temporary deposits and updates the context with the
 * appropriate deposit data based on the current deposit temporary ID or the last
 * deposit created by the current step.
 *
 * @param applicableData - The current workflow data containing temporary deposits
 * @param workflowCompletion - The workflow completion object with current step info
 * @param updateContextWithFields - Function to update the workflow context
 * @returns A function to save/update temporary deposits
 */
const useTemporaryDepositsSync = (
  applicableData: TransferContextData,
  workflowCompletion: WorkflowCompletion | undefined,
  updateContextWithFields: (
    fields: Partial<TransferContextData>,
  ) => Promise<void>,
) => {
  useEffect(() => {
    const temporaryDeposits = applicableData.temporaryDeposits || [];
    const currentDeposit = temporaryDeposits.find(
      (d: TransferContextData) =>
        d.temporaryId === applicableData.currentDepositTemporaryId,
    );
    if (
      currentDeposit &&
      currentDeposit.temporaryTransferState !== 'completed'
    ) {
      updateContextWithFields(
        toTransferContextData(
          omit(currentDeposit, ['substepId', 'temporaryId']),
        ),
      );
      return;
    }
  }, [applicableData.currentDepositTemporaryId]);

  const saveTemporaryDeposit = useCallback(async () => {
    const temporaryDeposits = [...(applicableData.temporaryDeposits || [])];
    const matchingTemporaryDeposit = temporaryDeposits.find(
      (d) => d.temporaryId === applicableData.currentDepositTemporaryId,
    );
    const shouldCreateNewDeposit =
      !matchingTemporaryDeposit ||
      matchingTemporaryDeposit.temporaryTransferState === 'completed';

    if (shouldCreateNewDeposit) {
      const newDeposit = toTransferContextData({
        ...applicableData,
        temporaryId: crypto.randomUUID(),
        substepId: workflowCompletion?.currentSubStepId,
      });
      await updateContextWithFields({
        temporaryDeposits: [...temporaryDeposits, newDeposit],
        currentDepositTemporaryId: newDeposit.temporaryId,
      });
    } else {
      const updatedDeposit = toTransferContextData({
        ...matchingTemporaryDeposit,
        ...toTransferContextData(applicableData),
        temporaryId: matchingTemporaryDeposit.temporaryId,
      });
      await updateContextWithFields({
        temporaryDeposits: temporaryDeposits.map((d) =>
          d.temporaryId === applicableData.currentDepositTemporaryId
            ? updatedDeposit
            : d,
        ),
        currentDepositTemporaryId: updatedDeposit.temporaryId,
      });
    }
  }, [
    applicableData,
    workflowCompletion?.currentSubStepId,
    updateContextWithFields,
  ]);

  return { saveTemporaryDeposit };
};

/**
 * Combined hook that manages applicableData, its setters, and temporary deposits sync
 * across deposit workflow components. This hook consolidates the functionality of
 * useApplicableData and useTemporaryDepositsSync into a single, cohesive interface.
 *
 * @param dummyDeposit - Optional dummy deposit data for testing/preview purposes
 * @param setDummyDeposit - Optional setter for dummy deposit data
 * @param workflowCompletion - The workflow completion object with current step info
 * @returns Object containing applicableData, update functions, and temporary deposit management
 */
export const useDepositWorkflowData = (
  dummyDeposit?: TransferContextData,
  setDummyDeposit?: Dispatch<SetStateAction<TransferContextData>>,
  workflowCompletion?: WorkflowCompletion,
) => {
  const { workflowData } = useContext(WorkflowContext);

  // Use the existing useApplicableData hook
  const {
    applicableData,
    setApplicationContext,
    setWorkflowCompletionContext,
    mode,
  } = useApplicableData(dummyDeposit, setDummyDeposit);

  // Enhanced update function that combines context update with field updates
  const updateContextWithFields = useCallback(
    async (newFields: Partial<TransferContextData>) => {
      await updateContextTransfer({
        value: newFields,
        existingContext: { ...workflowData, ...applicableData },
        setApplicationContext,
        setWorkflowCompletionContext,
      });
    },
    [
      applicableData,
      workflowData,
      setApplicationContext,
      setWorkflowCompletionContext,
    ],
  );

  // Use the existing useTemporaryDepositsSync hook
  const { saveTemporaryDeposit } = useTemporaryDepositsSync(
    applicableData,
    workflowCompletion,
    updateContextWithFields,
  );

  return {
    // Original useApplicableData interface
    applicableData,
    setApplicationContext,
    setWorkflowCompletionContext,

    // Enhanced update function
    updateContextWithFields,

    // Temporary deposits functionality
    saveTemporaryDeposit,
    mode,
  };
};
